import { isOrderComplete, isPackageDelivered, isPickupOrder } from "util/stopStatusUtils";
import {
  getStopActionLabel,
  normalizeStopAction,
  STOP_ACTIONS,
  resolveOrderType,
  resolveOrderKind,
  getStopRecordForOrder,
} from "util/warehouseReturnUtils";

export { resolveOrderType, resolveOrderKind, getStopRecordForOrder };

export function getOrderDeliveryNotes(order) {
  if (Array.isArray(order?.DeliveryNotes) && order.DeliveryNotes.length > 0) {
    return order.DeliveryNotes;
  }

  if (Array.isArray(order?.deliveries) && order.deliveries.length > 0) {
    return order.deliveries.map((entry, index) => {
      const packagesRaw = entry?.Packages ?? entry?.packages;
      let packages = [];

      if (Array.isArray(packagesRaw)) {
        packages = packagesRaw;
      } else if (typeof packagesRaw === "number" && packagesRaw > 0) {
        packages = Array.from({ length: packagesRaw }, (_, i) => ({ id: i }));
      }

      return {
        id: entry?.id ?? `${order?.id ?? "order"}-dn-${index}`,
        delivery_note_number:
          entry?.delivery_note_number ??
          entry?.deliveryNote ??
          entry?.delivery_note ??
          (entry?.id != null ? String(entry.id) : "-"),
        Packages: packages,
        _packagesCount: typeof packagesRaw === "number" ? packagesRaw : packages.length,
      };
    });
  }

  if (Array.isArray(order?.Packages) && order.Packages.length > 0) {
    return [
      {
        id: `${order?.id ?? "order"}-dn-0`,
        delivery_note_number:
          order?.delivery_note_number ??
          order?.delivery_note ??
          (order?.id != null ? String(order.id) : "-"),
        Packages: order.Packages,
        _packagesCount: order.Packages.length,
      },
    ];
  }

  if (Array.isArray(order?.packages) && order.packages.length > 0) {
    return [
      {
        id: `${order?.id ?? "order"}-dn-0`,
        delivery_note_number:
          order?.delivery_note_number ??
          order?.delivery_note ??
          (order?.id != null ? String(order.id) : "-"),
        Packages: order.packages,
        _packagesCount: order.packages.length,
      },
    ];
  }

  return [];
}



export function getDeliveryNotePackageCounts(note, order, pathwayStop, options = {}) {
  if (order?.removed_at) {
    return { delivered: 0, total: 0 };
  }
  const packages = Array.isArray(note?.Packages) ? note.Packages : [];
  const total =
    packages.length > 0
      ? packages.length
      : note?._packagesCount ?? 0;
  const countablePackages = packages.filter(
    (pkg) => !pkg?.removed_at && !pkg?.is_package_lost
  );
  const countableTotal =
    packages.length > 0 ? countablePackages.length : total;

  const totalGoodsBack =
    Number(pathwayStop?.total_goods_back) ||
    Number(order?.total_goods_back) ||
    0;
  const isGoodsBack =
    Boolean(pathwayStop?.goods_back) ||
    Boolean(order?.goods_back) ||
    totalGoodsBack > 0;

  const isReturnOrPickup =
    order?.order_type === "return" ||
    order?.is_return ||
    pathwayStop?.order_type === "return" ||
    isPickupOrder(order);



  if (isReturnOrPickup) {
    if (isGoodsBack) {
      const delivered = Math.min(countableTotal, totalGoodsBack);
      return { delivered, total };
    }
    if (options?.scanningCodesEnabled) {
      const delivered = countablePackages.filter((pkg) => isPackageDelivered(pkg, order, options)).length;
      if (delivered < total && (isOrderComplete(order, options) || order?.delivered_at || pathwayStop?.completed_at)) {
        return { delivered: total, total };
      }
      return { delivered, total };
    }
    const delivered = (isOrderComplete(order, options) || order?.delivered_at || pathwayStop?.completed_at) ? total : 0;
    return { delivered, total };
  }
  if (isGoodsBack) {
    const reservationAmount = Number(pathwayStop?.reservation_amount) || Number(order?.reservation_amount) || 0;
    const delivered = countableTotal - (totalGoodsBack + reservationAmount);
    return { delivered, total };
  }
  const delivered = isOrderComplete(order, options) ? total : 0;
  return { delivered, total };
}

export function hasDeliveredPackagesForOrders(orders, stopTab, pathwayStop, options = {}) {
  const { route, pathwayIndex } = options;
  return (orders || [])
    .filter((order) => orderMatchesStopTab(order, stopTab))
    .some((order) =>
      getOrderDeliveryNotes(order).some((note) => {
        const stopRecord = route
          ? getStopRecordForOrder(route, order, pathwayStop, pathwayIndex)
          : pathwayStop;
        const { delivered } = getDeliveryNotePackageCounts(note, order, stopRecord, options);
        return delivered > 0;
      })
    );
}

export function orderMatchesStopTab(order, stopTab) {
  const kind = resolveOrderKind(order);
  if (stopTab === "delivery") {
    return kind === "delivery" || kind === "pickup";
  }
  if (stopTab === "return") {
    return kind === "return";
  }
  if (stopTab === "warehouse_return") {
    return kind === "warehouse_return";
  }
  return kind === stopTab;
}

export function getDeliveryNotePackagesCount(deliveryNote) {
  const packages = Array.isArray(deliveryNote?.Packages) ? deliveryNote.Packages : [];
  if (packages.length > 0) {
    return packages.length;
  }
  return deliveryNote?._packagesCount ?? 0;
}

export function getOrderPackagesCount(order) {
  return getOrderDeliveryNotes(order).reduce(
    (sum, dn) => sum + getDeliveryNotePackagesCount(dn),
    0
  );
}

export function filterOrdersWithPackages(orders) {
  return (orders || []).filter((order) => getOrderPackagesCount(order) > 0);
}

export function getOrdersForStopDisplay(stop, orders) {
  const isFixStop = stop?.id === null;
  if (isFixStop) {
    return filterOrdersWithPackages(orders);
  }
  return orders || [];
}

export function getDeliveryNotesForStopDisplay(stop, order) {
  const deliveryNotes = getOrderDeliveryNotes(order);
  if (stop?.id === null) {
    return deliveryNotes.filter((dn) => getDeliveryNotePackagesCount(dn) > 0);
  }
  return deliveryNotes;
}




export function getStopOrdersBreakdownLines(orders, t, { isFixStop = false } = {}) {
  const relevantOrders = isFixStop ? filterOrdersWithPackages(orders) : (orders || []);
  const breakdown = {
    delivery: { orders: 0, packages: 0 },
    pickup: { orders: 0, packages: 0 },
    return: { orders: 0, packages: 0 },
  };

  relevantOrders.forEach((order) => {
    const kind = resolveOrderKind(order);
    const bucket = breakdown[kind] ?? breakdown.delivery;
    bucket.orders += 1;
    bucket.packages += getOrderPackagesCount(order);
  });

  const labelByKind = {
    delivery: t("Deliveries"),
    pickup: t("Pickup"),
    return: t("Returns"),
  };

  return ["delivery", "pickup", "return"]
    .filter((kind) => breakdown[kind].orders > 0)
    .map((kind) => ({
      kind,
      label: labelByKind[kind],
      orders: breakdown[kind].orders,
      packages: breakdown[kind].packages,
    }));
}
